import React, { useState } from 'react';
import { auth, realTimeDb } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import './StartPage.css';
import { DEFAULT_PLAN, getPlanLimit } from '../config/planConfig';

const StartPage = () => {
  const [currentView, setCurrentView] = useState('landing');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const navigate = useNavigate();

  const predefinedAdmin = {
    email: 'info@webandforms.co.za',
    password: 'Azam19841988001#',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const isAdminLogin = email === predefinedAdmin.email && password === predefinedAdmin.password;

    try {
      if (authMode === 'login') {
        console.log("Logging in user...");

        // Bypass Firebase Auth for predefined admin
        if (isAdminLogin) {
          console.log("Hardcoded admin login successful.");
          navigate('/admindashboard');
          return;
        }

        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userRef = ref(realTimeDb, `users/${user.uid}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          const userData = snapshot.val();

          if (userData.isSuspended) {
            console.log("User is suspended. Signing out...");
            await auth.signOut();
            setError('Your account is suspended. Please contact support.');
          } else {
            console.log("User login successful.");
            navigate('/dashboard');
          }
        } else {
          console.error("User data not found in Realtime Database.");
          setError('User data not found. Please contact support.');
        }
      } else if (authMode === 'signup') {
        console.log("Signing up user...");
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log("User created in Authentication:", userCredential.user);

        const userId = userCredential.user.uid;
        const newUserRef = ref(realTimeDb, `users/${userId}`);
        const currentMonth = new Date().toISOString().slice(0, 7);
        await set(newUserRef, {
          email: email,
          isSuspended: false, // New users start active, only suspended manually by admin
          isAdmin: false,
          name: '',
          plan: 'FREE', // Always start on FREE tier
          currentTier: 'FREE', // Current active tier
          monthlyLimit: 50, // FREE tier limit
          billingStatus: 'active', // New users are active immediately
          subscriptionStatus: 'free', // Track subscription type
          payfastSubscriptionId: null, // No PayFast subscription initially
          usage: {
            month: currentMonth,
            count: 0,
          },
          form: {
            clientEmail: '',
            style: 'default',
            title: 'Untitled Form',
            webhookUrl: '',
          },
        });
        console.log("User data saved to Realtime Database.");

        alert('Registration successful! Your account is active and ready to use.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error("Error during authentication process:", error);
      setError(error.message);
    }
  };

  if (currentView === 'landing') {
    return (
      <div className="landing-page">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            <div className="logo">
              <span className="logo-year">2030</span>
              <span className="logo-ai">AI</span>
              <span className="logo-text">FormBuilder</span>
            </div>
            <nav className="nav">
              <button className="nav-link" onClick={() => {/* scroll to features */}}>Features</button>
              <button className="nav-link" onClick={() => setCurrentView('pricing')}>Pricing</button>
              <button className="nav-link">Contact</button>
              <button className="nav-btn" onClick={() => setCurrentView('auth')}>
                Sign In
              </button>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="hero">
          <div className="hero-background">
            <div className="hero-particles"></div>
            <div className="hero-grid"></div>
          </div>
          <div className="hero-content">
            <h1>
              Build Forms Anywhere. Collect
              <br />
              <span className="hero-highlight">Data Everything</span>
              <span className="ai-dot">.</span>
            </h1>
            <p>
              The ultimate AI-powered form building solution that connects your business seamlessly.
              Create, manage, and analyze forms from any device, anywhere in the world with advanced AI assistance.
            </p>
            <div className="hero-buttons">
              <button className="btn-primary" onClick={() => setCurrentView('auth')}>
                <span className="btn-icon">🚀</span>
                Get Started Free
              </button>
              <button className="btn-secondary">
                <span className="btn-icon">▶️</span>
                Watch Demo
              </button>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">50K+</span>
                <span className="stat-label">AI-Generated Forms</span>
              </div>
              <div className="stat">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">Uptime</span>
              </div>
              <div className="stat">
                <span className="stat-number">24/7</span>
                <span className="stat-label">AI Support</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="why-choose">
          <div className="section-header">
            <h2>Why Choose 2030AI FormBuilder?</h2>
            <p className="section-subtitle">
              Experience the future of data collection with our cutting-edge AI technology
            </p>
          </div>
          
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">
                <div className="icon-bg">🤖</div>
              </div>
              <h3>AI-Powered Intelligence</h3>
              <p>Smart form suggestions, automated validation, and intelligent data processing powered by advanced machine learning algorithms.</p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <div className="icon-bg">⚡</div>
              </div>
              <h3>Lightning Fast Performance</h3>
              <p>Built on cutting-edge cloud infrastructure for instant form creation and real-time data processing at unprecedented scale.</p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <div className="icon-bg">🛡️</div>
              </div>
              <h3>Quantum Security</h3>
              <p>Next-generation encryption and security protocols protect your data with military-grade protection and AI threat detection.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features">
          <div className="features-container">
            <h2>Powerful Features for Every Business</h2>
            <p className="section-subtitle">
              Everything you need to create, deploy, and manage forms effectively
            </p>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">�</div>
                <h3>7 Field Types</h3>
                <p>Text, Email, Number, Date, Checkbox, Dropdown, and Query API fields. Build any form you need.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🗂️</div>
                <h3>Stackable Forms</h3>
                <p>Create multi-section forms where users select between different subforms. Perfect for complex workflows.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔐</div>
                <h3>Password Protection</h3>
                <p>Protect sensitive forms with password encryption. Control who can access and submit forms.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🪝</div>
                <h3>Webhooks & Integrations</h3>
                <p>Automatically send submissions to external endpoints. Integrate with your favorite tools seamlessly.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🎨</div>
                <h3>Custom Branding</h3>
                <p>Choose from multiple themes and match your brand identity perfectly with custom styling options.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h3>Advanced Analytics</h3>
                <p>Track submissions in real-time, view detailed reports, and export data to PDF.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📱</div>
                <h3>Mobile Responsive</h3>
                <p>Forms work perfectly on desktop, tablet, and mobile devices. Reach users everywhere.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3>Fast Performance</h3>
                <p>Lightning-fast form loading and submission processing. Optimized for speed and reliability.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔄</div>
                <h3>Real-Time Sync</h3>
                <p>See submissions appear instantly. Live updates across all your devices in real-time.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="use-cases">
          <h2>Built for Every Business Type</h2>
          <p className="section-subtitle">From startups to enterprises, Form Builder adapts to your needs</p>
          
          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-icon">�</div>
              <h3>Enterprises</h3>
              <p>Large-scale form deployment with advanced analytics, API access, and dedicated support.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">📈</div>
              <h3>Agencies</h3>
              <p>Create and manage forms for multiple clients with customizable branding and white-label options.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">🏪</div>
              <h3>E-Commerce</h3>
              <p>Collect customer feedback, survey responses, and order information with custom workflows.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">🏥</div>
              <h3>Healthcare</h3>
              <p>HIPAA-compliant secure data collection with password protection and encryption.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">🎓</div>
              <h3>Education</h3>
              <p>Student enrollment, course registration, and feedback collection made simple.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">🤝</div>
              <h3>Non-Profits</h3>
              <p>Volunteer sign-ups, donation collection, and event registration in one platform.</p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits">
          <div className="benefits-container">
            <div className="benefits-content">
              <h2>Why Teams Choose Form Builder</h2>
              <ul className="benefits-list">
                <li>✓ <strong>Save Time</strong> - Create forms in minutes, not hours</li>
                <li>✓ <strong>No Coding</strong> - Visual form builder, no technical skills needed</li>
                <li>✓ <strong>Secure Data</strong> - Enterprise-grade encryption and compliance</li>
                <li>✓ <strong>Scale Easily</strong> - From 50 to 1,000,000 submissions per month</li>
                <li>✓ <strong>Smart Integrations</strong> - Connect to your existing tools via webhooks</li>
                <li>✓ <strong>Expert Support</strong> - Email, priority, or dedicated support depending on your plan</li>
              </ul>
            </div>
            <div className="benefits-image">
              <div className="benefits-placeholder">📊</div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta">
          <h2>Ready to Get Started?</h2>
          <p>Join thousands of businesses using Form Builder to collect data effectively</p>
          <div className="cta-buttons">
            <button className="btn-primary-large" onClick={() => setCurrentView('auth')}>
              Start Free Trial
            </button>
            <button className="btn-secondary-large" onClick={() => setCurrentView('pricing')}>
              View Pricing
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-section">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing" onClick={() => setCurrentView('pricing')}>Pricing</a>
              <a href="#security">Security</a>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#contact">Contact</a>
              <a href="#blog">Blog</a>
            </div>
            <div className="footer-section">
              <h4>Resources</h4>
              <a href="#docs">Documentation</a>
              <a href="#api">API Reference</a>
              <a href="#help">Help Center</a>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
              <a href="#cookies">Cookie Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Form Builder. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === 'pricing') {
    return (
      <div className="landing-page">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            <div className="logo" onClick={() => setCurrentView('landing')} style={{cursor: 'pointer'}}>Form Builder</div>
            <nav className="nav">
              <button className="nav-link" onClick={() => setCurrentView('landing')}>Back</button>
              <button className="nav-btn" onClick={() => setCurrentView('auth')}>
                Sign In
              </button>
            </nav>
          </div>
        </header>

        {/* Pricing Page */}
        <section className="pricing-page">
          <div className="pricing-hero">
            <h1>Simple, Transparent Pricing</h1>
            <p>Choose the perfect plan for your needs. All plans include all features.</p>
          </div>

          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Free</h3>
                <div className="pricing-price">Free</div>
              </div>
              <ul className="pricing-features">
                <li>✓ 50 submissions/month</li>
                <li>✓ 3 forms</li>
                <li>✓ All field types</li>
                <li>✓ Password protection</li>
                <li>✓ Webhooks</li>
                <li>✓ Stackable forms</li>
                <li>✓ Email support</li>
              </ul>
              <button className="pricing-btn" onClick={() => setCurrentView('auth')}>Get Started</button>
            </div>

            {/* Tier 1 */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Tier 1</h3>
                <div className="pricing-price">R299<span>/month</span></div>
              </div>
              <ul className="pricing-features">
                <li>✓ 200 submissions/month</li>
                <li>✓ 3 forms</li>
                <li>✓ All field types</li>
                <li>✓ Password protection</li>
                <li>✓ Webhooks</li>
                <li>✓ Stackable forms</li>
                <li>✓ Email support</li>
              </ul>
              <button className="pricing-btn" onClick={() => setCurrentView('auth')}>Choose Plan</button>
            </div>

            {/* Tier 2 */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Tier 2</h3>
                <div className="pricing-price">R399<span>/month</span></div>
              </div>
              <ul className="pricing-features">
                <li>✓ 400 submissions/month</li>
                <li>✓ 3 forms</li>
                <li>✓ All field types</li>
                <li>✓ Password protection</li>
                <li>✓ Webhooks</li>
                <li>✓ Stackable forms</li>
                <li>✓ Custom branding</li>
                <li>✓ Priority support</li>
              </ul>
              <button className="pricing-btn" onClick={() => setCurrentView('auth')}>Choose Plan</button>
            </div>

            {/* Tier 3 */}
            <div className="pricing-card featured">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-header">
                <h3>Tier 3</h3>
                <div className="pricing-price">R499<span>/month</span></div>
              </div>
              <ul className="pricing-features">
                <li>✓ 1000 submissions/month</li>
                <li>✓ 5 forms</li>
                <li>✓ All field types</li>
                <li>✓ Password protection</li>
                <li>✓ Webhooks</li>
                <li>✓ Stackable forms</li>
                <li>✓ Custom branding</li>
                <li>✓ Advanced analytics</li>
                <li>✓ Priority support</li>
              </ul>
              <button className="pricing-btn featured" onClick={() => setCurrentView('auth')}>Choose Plan</button>
            </div>

            {/* Tier 4 */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Tier 4</h3>
                <div className="pricing-price">R1400<span>/month</span></div>
              </div>
              <ul className="pricing-features">
                <li>✓ Unlimited submissions</li>
                <li>✓ 10 forms</li>
                <li>✓ All field types</li>
                <li>✓ Password protection</li>
                <li>✓ Webhooks</li>
                <li>✓ Stackable forms</li>
                <li>✓ Custom branding</li>
                <li>✓ Advanced analytics</li>
                <li>✓ Custom integrations</li>
                <li>✓ API access</li>
                <li>✓ Dedicated support</li>
              </ul>
              <button className="pricing-btn" onClick={() => setCurrentView('auth')}>Choose Plan</button>
            </div>
          </div>

          {/* FAQ Section */}
          <section className="pricing-faq">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h4>Can I upgrade or downgrade my plan?</h4>
                <p>Yes! You can upgrade or downgrade your plan anytime. Changes take effect immediately.</p>
              </div>
              <div className="faq-item">
                <h4>Do all plans include webhooks?</h4>
                <p>Yes! All plans include full webhook support for integrations with external services.</p>
              </div>
              <div className="faq-item">
                <h4>What happens if I exceed my submission limit?</h4>
                <p>We'll notify you when you're approaching your limit. You can upgrade anytime to continue.</p>
              </div>
              <div className="faq-item">
                <h4>Is there a free trial?</h4>
                <p>Yes! Start with our Free plan with no credit card required. Upgrade whenever you're ready.</p>
              </div>
            </div>
          </section>
        </section>

        {/* Footer */}
        <footer className="footer">
          <p>&copy; 2025 Form Builder. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-form-box">
          <div className="auth-header">
            <h2>{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
            <p>{authMode === 'login' ? 'Sign in to your account' : 'Join us today'}</p>
          </div>

          <form onSubmit={handleSubmit} className="form">
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              required
            />
            {error && <p className="error-message">{error}</p>}
            <button type="submit" className="form-submit-btn">
              {authMode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="auth-toggle-section">
            {authMode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <button
                  type="button"
                  className="auth-toggle-link"
                  onClick={() => setAuthMode('signup')}
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <button
                  type="button"
                  className="auth-toggle-link"
                  onClick={() => setAuthMode('login')}
                >
                  Sign in
                </button>
              </p>
            )}
          </div>

          <button
            type="button"
            className="back-to-landing"
            onClick={() => {
              setCurrentView('landing');
              setAuthMode('login');
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartPage;
